import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/seoApi";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import AuditReport from "@/components/AuditReport";
import { AlertTriangle, ArrowLeft, History, Radar } from "lucide-react";

const scoreColor = (s) => (s >= 80 ? "text-emerald-600" : s >= 50 ? "text-amber-600" : "text-red-600");

const T = {
  en: {
    back: "All audits",
    title: "My audits",
    sub: "Every sitewide audit you run is saved here.",
    newAudit: "New audit",
    loadErr: "Could not load audits",
    empty1: "No audits yet. Run your first",
    emptyLink: "sitewide audit",
    empty2: "- it will be saved here automatically.",
    pages: "pages",
  },
  fr: {
    back: "Tous les audits",
    title: "Mes audits",
    sub: "Chaque audit complet que vous lancez est conservé ici.",
    newAudit: "Nouvel audit",
    loadErr: "Impossible de charger les audits",
    empty1: "Aucun audit pour l'instant. Lancez votre premier",
    emptyLink: "audit complet",
    empty2: "- il sera conservé ici automatiquement.",
    pages: "pages",
  },
};

const formatDate = (iso, lang) => {
  try {
    return new Date(iso).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function MyAudits() {
  const { user, ready } = useAuth();
  const { lang } = useLang();
  const t = T[lang];
  const navigate = useNavigate();
  const [audits, setAudits] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (ready && !user) navigate("/login?next=/my-audits", { replace: true });
  }, [ready, user, navigate]);

  useEffect(() => {
    if (!user) return;
    api
      .get("/audits")
      .then(({ data }) => setAudits(data.audits))
      .catch((err) => setError(getErrorMessage(err)));
  }, [user]);

  const openAudit = async (id) => {
    setLoadingDetail(true);
    setError(null);
    try {
      const { data } = await api.get(`/audits/${id}`);
      setSelected(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoadingDetail(false);
    }
  };

  if (!ready || !user) return null;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-8 sm:py-10">
      {selected ? (
        <>
          <Button variant="ghost" size="sm" onClick={() => setSelected(null)} data-testid="my-audits-back-button">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> {t.back}
          </Button>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{selected.domain}</h1>
            <Badge variant="secondary" className="text-xs">{formatDate(selected.created_at, lang)}</Badge>
          </div>
          <AuditReport result={selected.report} />
        </>
      ) : (
        <>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t.title}</h1>
              <p className="mt-2 text-muted-foreground">{t.sub}</p>
            </div>
            <Button asChild data-testid="my-audits-new-button">
              <Link to="/site-audit"><Radar className="h-4 w-4 mr-2" />{t.newAudit}</Link>
            </Button>
          </div>

          {error && (
            <Alert variant="destructive" className="mt-6 max-w-2xl">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{t.loadErr}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!audits && !error && (
            <div className="mt-8 space-y-3">
              <Skeleton className="h-20 rounded-lg" />
              <Skeleton className="h-20 rounded-lg" />
            </div>
          )}

          {audits && audits.length === 0 && (
            <div className="mt-12 rounded-lg border border-dashed p-10 text-center max-w-2xl" data-testid="my-audits-empty">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground">
                <History className="h-6 w-6" />
              </div>
              <p className="mt-3 text-sm text-muted-foreground">
                {t.empty1} <Link to="/site-audit" className="text-primary underline underline-offset-2">{t.emptyLink}</Link> {t.empty2}
              </p>
            </div>
          )}

          {audits && audits.length > 0 && (
            <div className="mt-8 space-y-3" data-testid="my-audits-list">
              {audits.map((a) => (
                <Card key={a.id} className="hover:border-primary/40 transition-colors cursor-pointer" onClick={() => openAudit(a.id)} data-testid={`my-audits-item-${a.id}`}>
                  <CardContent className="p-4 sm:p-5 flex items-center gap-4">
                    <span className={`text-3xl font-heading font-semibold tabular-nums ${scoreColor(a.overall_score)}`}>
                      {a.overall_score}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{a.domain}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {formatDate(a.created_at, lang)} - {a.pages_scanned} {t.pages}
                      </p>
                    </div>
                    <Badge variant="outline" className="shrink-0 text-xs capitalize">{(a.grade || "").replace("_", " ")}</Badge>
                  </CardContent>
                </Card>
              ))}
              {loadingDetail && <Skeleton className="h-40 rounded-lg" />}
            </div>
          )}
        </>
      )}
    </div>
  );
}
